/**
 * The link between two stages: a caption and a dashed line carrying the lane's
 * tone.
 *
 * `animated` is the caller's call rather than derived from `tone`, because the
 * panels disagree about which state deserves motion — the Test lab marches
 * while TM dials, a replay marches while bytes actually move — and both are
 * right about their own pipeline.
 */

import type { ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import {
  MONO_FONT,
  flowDashes,
  flowToken,
  toneLineColor,
  type FlowTone,
} from './tokens';

interface FlowConnectorProps {
  label?: ReactNode;
  tone: FlowTone;
  animated?: boolean;
}

export default function FlowConnector({
  label,
  tone,
  animated = false,
}: FlowConnectorProps) {
  const theme = useTheme();
  const color = toneLineColor(theme, tone);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.625,
        px: 1.5,
        minWidth: 0,
      }}
    >
      {label && (
        <Typography
          variant="caption"
          sx={{
            fontFamily: MONO_FONT,
            fontSize: 10,
            textAlign: 'center',
            color: flowToken(theme, 'wash500'),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          height: 2,
          // 6px on, 6px off — a 12px period, which is what flowDashes travels a
          // whole number of.
          background: `repeating-linear-gradient(90deg, ${color} 0 6px, transparent 6px 12px)`,
          animation: animated ? `${flowDashes} .5s linear infinite` : 'none',
        }}
      />
    </Box>
  );
}
